import type { FormSchemaType } from '@/schema';
import { ChevronRightIcon, DocumentTextIcon, UserIcon } from '@heroicons/react/24/outline';
import { Question, InfoLine, RecapSection, FieldInput, BulleAide } from '@workspace/core-ts/components';
import { SmartFormGlobal, useGlobalForm, useSmartFormGlobal } from '@workspace/core-ts/contexts';
import { useContent } from '@workspace/core-ts/hooks';
import { normalizeEuro, isValidMarquageLength } from '@workspace/utils';
import { useMemo } from 'react';
import { VeloIcon, VaeIcon, CargoIcon } from '@/components/icons';
import recapitulatifContent from '../content/3-recapitulatif';

export default function Recapitulatif() {
    const methods = useSmartFormGlobal<FormSchemaType>();
    const { actions } = useGlobalForm();
    const r = useContent();

    const formData = methods.watch();
    const Tarif = methods.watch('Tarif');
    const Devis = methods.watch('Devis');
    const fractionnement = methods.watch('DemandeTarif.Police.FractionnementChoisi') || 'M';
    const formuleChoisie = methods.watch('DemandeTarif.Police.FormuleChoisie');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const listeOptions = methods.watch('DemandeTarif.ListeOptions') || [];

    // Données véhicule et souscripteur
    const vehicule = formData?.DemandeTarif?.Vehicule;
    const souscripteur = formData?.DemandeTarif?.ListePersonnes?.[0];

    // Formule sélectionnée depuis Devis (prioritaire) ou Tarif (fallback)
    const formule = useMemo(() => {
        // Priorité à Devis.Formule
        if (Devis?.Formule) return Devis.Formule;
        // Fallback sur Tarif.ListeFormules
        if (!Tarif?.ListeFormules || !formuleChoisie) return null;
        return Tarif.ListeFormules.find((f: any) => f.CodeFormule === formuleChoisie);
    }, [Devis?.Formule, Tarif?.ListeFormules, formuleChoisie]);

    // Vérifier si un code promo est actif
    const codePromoActif = Boolean(Devis?.Formule?.CodePromo?.Eligible || Tarif?.CodePromo?.Eligible);

    // Options souscrites
    const optionsSouscrites = useMemo(() => {
        if (!formule?.ListeGaranties) return [];

        return listeOptions
            .filter((opt: any) => opt.Souscrite)
            .map((opt: any) => {
                const garantie = formule.ListeGaranties?.find((g: any) => g.CodeGarantie === opt.CodeOption);
                return {
                    nom: garantie?.LibelleGarantie || opt.CodeOption,
                    tarif: {
                        mensuel: codePromoActif && garantie?.PrimeClientMensuellePromo
                            ? garantie.PrimeClientMensuellePromo
                            : garantie?.PrimeClientMensuelle || 0,
                        annuel: codePromoActif && garantie?.PrimeClientPromo
                            ? garantie.PrimeClientPromo
                            : garantie?.PrimeClient || 0,
                        mensuelOriginal: codePromoActif && garantie?.PrimeClientMensuellePromo
                            ? garantie?.PrimeClientMensuelle
                            : undefined,
                        annuelOriginal: codePromoActif && garantie?.PrimeClientPromo
                            ? garantie?.PrimeClient
                            : undefined,
                    },
                };
            });
    }, [formule, listeOptions, codePromoActif]);

    // Calcul des tarifs - Utiliser Devis.Formule (prioritaire) ou formule de Tarif
    const tarifs = useMemo(() => {
        if (!formule) return { formule: 0, formuleOriginal: undefined, options: 0, total: 0, totalOriginal: undefined };

        const tarifFormule = fractionnement === 'M'
            ? (codePromoActif && formule.PrimeMensuelleBasePromo ? formule.PrimeMensuelleBasePromo : formule.PrimeMensuelleBase || 0)
            : (codePromoActif && formule.PrimeAnnuelleFracAnnuelBasePromo ? formule.PrimeAnnuelleFracAnnuelBasePromo : formule.PrimeAnnuelleFracAnnuelBase || 0);

        const tarifFormuleOriginal = codePromoActif && (
            fractionnement === 'M' ? formule.PrimeMensuelleBasePromo : formule.PrimeAnnuelleFracAnnuelBasePromo
        )
            ? (fractionnement === 'M' ? formule.PrimeMensuelleBase : formule.PrimeAnnuelleFracAnnuelBase)
            : undefined;

        const tarifOptions = optionsSouscrites.reduce((acc, opt) =>
            acc + (fractionnement === 'M' ? opt.tarif.mensuel : opt.tarif.annuel), 0);

        const total = tarifFormule + tarifOptions;
        const totalOriginal = tarifFormuleOriginal
            ? tarifFormuleOriginal + optionsSouscrites.reduce((acc, opt) =>
                acc + (fractionnement === 'M' ? (opt.tarif.mensuelOriginal || opt.tarif.mensuel) : (opt.tarif.annuelOriginal || opt.tarif.annuel)), 0)
            : undefined;

        return {
            formule: tarifFormule,
            formuleOriginal: tarifFormuleOriginal,
            options: tarifOptions,
            total,
            totalOriginal,
        };
    }, [formule, optionsSouscrites, fractionnement, codePromoActif]);

    // Utilitaires
    const getNomComplet = () => {
        if (!souscripteur) return '';
        const civilite = souscripteur.Civilite === '1' ? 'Mme' : 'M.';
        return `${civilite} ${souscripteur.Prenom} ${souscripteur.Nom}`;
    };

    const getAdresseComplete = () => {
        if (!souscripteur) return '';
        return `${souscripteur.Adresse1 || ''} ${souscripteur.CP || ''} ${souscripteur.Ville || ''}`.trim();
    };

    const getVehiculeType = () => {
        switch (vehicule?.GenreVehicule) {
            case 'VEL':
                return 'Vélo Classique';
            case 'VAE':
                return 'Vélo à Assistance Électrique';
            case 'VELC':
                return 'Vélo Cargo';
            default:
                return vehicule?.GenreVehicule;
        }
    };

    const getVehiculeIcon = () => {
        switch (vehicule?.GenreVehicule) {
            case 'VEL':
                return VeloIcon;
            case 'VAE':
                return VaeIcon;
            case 'VELC':
                return CargoIcon;
            default:
                return VeloIcon;
        }
    };

    return (
        <SmartFormGlobal
            {...methods}
            className="mx-auto"
        >
            <Question className="max-w-4xl mx-auto">
                <div className="space-y-6">
                    {/* Récapitulatif tarifaire */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold mb-4 text-gray-900">{formule?.LibelleFormule || 'Votre formule'}</h3>

                        <div className="space-y-3">
                            <div className="space-y-2">
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-600">{formule?.LibelleFormule || 'Formule'}</span>
                                    <div className="text-right">
                                        <div className="font-medium text-gray-900">
                                            {normalizeEuro(tarifs.formule)}/{fractionnement === 'M' ? 'mois' : 'an'}
                                        </div>
                                        {tarifs.formuleOriginal && tarifs.formuleOriginal > tarifs.formule && (
                                            <div className="text-xs text-gray-500 line-through">
                                                {normalizeEuro(tarifs.formuleOriginal)}/{fractionnement === 'M' ? 'mois' : 'an'}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {optionsSouscrites.map((option, index) => {
                                    const tarifActuel = fractionnement === 'M' ? option.tarif.mensuel : option.tarif.annuel;
                                    const tarifOriginal = fractionnement === 'M' ? option.tarif.mensuelOriginal : option.tarif.annuelOriginal;

                                    return (
                                        <div key={index} className="flex justify-between items-center text-sm">
                                            <span className="text-gray-600">{option.nom}</span>
                                            <div className="text-right">
                                                <div className="font-medium text-gray-900">
                                                    +{normalizeEuro(tarifActuel)}/{fractionnement === 'M' ? 'mois' : 'an'}
                                                </div>
                                                {tarifOriginal && tarifOriginal > tarifActuel && (
                                                    <div className="text-xs text-gray-500 line-through">
                                                        +{normalizeEuro(tarifOriginal)}/{fractionnement === 'M' ? 'mois' : 'an'}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="bg-primary/10 rounded-lg p-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-contrast">Total</span>
                                    <div className="text-right">
                                        <div className="text-lg font-bold text-primary">
                                            {normalizeEuro(tarifs.total)}
                                            <span className="text-xs font-normal text-gray-500">/{fractionnement === 'M' ? 'mois' : 'an'}</span>
                                        </div>
                                        {tarifs.totalOriginal && tarifs.totalOriginal > tarifs.total && (
                                            <div className="text-sm text-gray-500 line-through">
                                                {normalizeEuro(tarifs.totalOriginal)}
                                                <span className="text-xs">/{fractionnement === 'M' ? 'mois' : 'an'}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Numéro de marquage */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">{r(recapitulatifContent.numeroMarquageTitle)}</label>
                        <BulleAide className="mb-3">{r(recapitulatifContent.numeroMarquageWarning)}</BulleAide>
                        <FieldInput
                            name="DemandeContrat.VIN"
                            placeholder="Ex: ABC123456789"
                            validation={{
                                validate: {
                                    isValidMarquageLength,
                                },
                            }}
                        />
                    </div>

                    {/* Sections d'information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Véhicule */}
                        <RecapSection
                            icon={getVehiculeIcon()}
                            title={r(recapitulatifContent.vehiculeTitle)}
                            onEdit={() => actions.goToStep('votre-velo')}
                        >
                            <InfoLine
                                label="Type"
                                value={getVehiculeType()}
                            />
                            <InfoLine
                                label="Marque"
                                value={vehicule?.Designation}
                            />
                            <InfoLine
                                label="Valeur"
                                value={vehicule?.ValeurVehicule ? normalizeEuro(vehicule.ValeurVehicule) : undefined}
                            />
                            <InfoLine
                                label="Date d'achat"
                                value={vehicule?.DateMEC}
                            />
                        </RecapSection>

                        {/* Souscripteur */}
                        <RecapSection
                            icon={UserIcon}
                            title={r(recapitulatifContent.informationsTitle)}
                            onEdit={() => actions.goToStep('informations')}
                        >
                            <InfoLine
                                label="Nom complet"
                                value={getNomComplet()}
                            />
                            <InfoLine
                                label="Date de naissance"
                                value={souscripteur?.DateNaissance}
                            />
                            <InfoLine
                                label="Email"
                                value={souscripteur?.Email}
                            />
                            <InfoLine
                                label="Téléphone"
                                value={souscripteur?.TelPortable}
                            />
                            <InfoLine
                                label="Adresse"
                                value={getAdresseComplete()}
                            />
                        </RecapSection>
                    </div>

                    {/* Documents */}
                    {(Devis || Tarif) && (
                        <RecapSection
                            icon={DocumentTextIcon}
                            title={r(recapitulatifContent.documentsTitle)}
                        >
                            <div className="grid gap-2">
                                {[
                                    { nom: "Proposition d'assurance", key: 'UrlPDFDevis' },
                                    { nom: 'Dispositions Générales', key: 'UrlPDFDispositionsGenerales' },
                                    { nom: "Document d'information", key: 'UrlPDFDocumentInformation' },
                                    { nom: "Convention d'assistance", key: 'UrlPDFConventionAssistance' },
                                    { nom: 'Lettre RGPD', key: 'UrlPDFLettreRGPD' },
                                    { nom: 'Fiche Conseil', key: 'UrlPDFFicheConseil' },
                                ].map((doc) => {
                                    // Check Devis first, then Tarif
                                    const devisTyped = Devis as Record<string, any>;
                                    const tarifTyped = Tarif as Record<string, any>;
                                    const url = devisTyped?.[doc.key] || tarifTyped?.[doc.key];

                                    if (!url) return null;

                                    return (
                                        <a
                                            key={doc.key}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 text-xs text-primary hover:underline"
                                        >
                                            <ChevronRightIcon className="w-3 h-3" />
                                            {doc.nom}
                                        </a>
                                    );
                                })}
                            </div>
                        </RecapSection>
                    )}
                </div>
            </Question>
        </SmartFormGlobal>
    );
}